import type { TableCell, TableElement } from '@/components/canvas/document/publicTypes';
import { getCell, getColWidths, getRowHeights, reorderColumns, reorderRows } from './tableLayout';
import { TABLE_MIN_COL_WIDTH, TABLE_MIN_ROW_HEIGHT } from '@/components/canvas/shared/constants/legacyConfig';
import { createTextContentData } from '@/components/canvas/elements/text/factory';

export type TableStructurePatch = {
  data: Partial<TableElement['data']>;
  size?: Partial<TableElement['size']>;
};

/** Excel 风格列名：0 → A, 25 → Z, 26 → AA */
export function columnHeaderLabel(col: number): string {
  let n = col;
  let label = '';
  while (n >= 0) {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  }
  return label;
}

function remapCellsOnColInsert(
  cells: Record<string, TableCell> | undefined,
  at: number,
): Record<string, TableCell> | undefined {
  if (!cells) return cells;
  const next: Record<string, TableCell> = {};
  for (const [key, cell] of Object.entries(cells)) {
    const [r, c] = key.split(',').map(Number);
    const nc = c >= at ? c + 1 : c;
    next[`${r},${nc}`] = cell;
  }
  return next;
}

function remapCellsOnColDelete(
  cells: Record<string, TableCell> | undefined,
  at: number,
): Record<string, TableCell> | undefined {
  if (!cells) return cells;
  const next: Record<string, TableCell> = {};
  for (const [key, cell] of Object.entries(cells)) {
    const [r, c] = key.split(',').map(Number);
    if (c === at) continue;
    const nc = c > at ? c - 1 : c;
    next[`${r},${nc}`] = cell;
  }
  return Object.keys(next).length ? next : undefined;
}

function remapCellsOnRowInsert(
  cells: Record<string, TableCell> | undefined,
  at: number,
): Record<string, TableCell> | undefined {
  if (!cells) return cells;
  const next: Record<string, TableCell> = {};
  for (const [key, cell] of Object.entries(cells)) {
    const [r, c] = key.split(',').map(Number);
    const nr = r >= at ? r + 1 : r;
    next[`${nr},${c}`] = cell;
  }
  return next;
}

function remapCellsOnRowDelete(
  cells: Record<string, TableCell> | undefined,
  at: number,
): Record<string, TableCell> | undefined {
  if (!cells) return cells;
  const next: Record<string, TableCell> = {};
  for (const [key, cell] of Object.entries(cells)) {
    const [r, c] = key.split(',').map(Number);
    if (r === at) continue;
    const nr = r > at ? r - 1 : r;
    next[`${nr},${c}`] = cell;
  }
  return Object.keys(next).length ? next : undefined;
}

function shiftMergedOnColInsert(
  ranges: [number, number, number, number][] | undefined,
  at: number,
): [number, number, number, number][] | undefined {
  if (!ranges?.length) return ranges;
  return ranges.map(([r0, c0, r1, c1]) => {
    const nc0 = c0 >= at ? c0 + 1 : c0;
    const nc1 = c1 >= at ? c1 + 1 : c1;
    return [r0, nc0, r1, nc1] as [number, number, number, number];
  });
}

function shiftMergedOnColDelete(
  ranges: [number, number, number, number][] | undefined,
  at: number,
): [number, number, number, number][] | undefined {
  if (!ranges?.length) return ranges;
  const next: [number, number, number, number][] = [];
  for (const [r0, c0, r1, c1] of ranges) {
    if (c0 === at && c1 === at) continue;
    if (c0 > at && c1 > at) next.push([r0, c0 - 1, r1, c1 - 1]);
    else if (c1 < at) next.push([r0, c0, r1, c1]);
    else if (c0 < at && c1 > at) next.push([r0, c0, r1, c1 - 1]);
  }
  return next.length ? next : undefined;
}

function shiftMergedOnRowInsert(
  ranges: [number, number, number, number][] | undefined,
  at: number,
): [number, number, number, number][] | undefined {
  if (!ranges?.length) return ranges;
  return ranges.map(([r0, c0, r1, c1]) => {
    const nr0 = r0 >= at ? r0 + 1 : r0;
    const nr1 = r1 >= at ? r1 + 1 : r1;
    return [nr0, c0, nr1, c1] as [number, number, number, number];
  });
}

function shiftMergedOnRowDelete(
  ranges: [number, number, number, number][] | undefined,
  at: number,
): [number, number, number, number][] | undefined {
  if (!ranges?.length) return ranges;
  const next: [number, number, number, number][] = [];
  for (const [r0, c0, r1, c1] of ranges) {
    if (r0 === at && r1 === at) continue;
    if (r0 > at && r1 > at) next.push([r0 - 1, c0, r1 - 1, c1]);
    else if (r1 < at) next.push([r0, c0, r1, c1]);
    else if (r0 < at && r1 > at) next.push([r0, c0, r1 - 1, c1]);
  }
  return next.length ? next : undefined;
}

function remapRecordKeysOnColInsert<T>(
  record: Record<string, T> | undefined,
  at: number,
): Record<string, T> | undefined {
  if (!record) return record;
  const next: Record<string, T> = {};
  for (const [k, v] of Object.entries(record)) {
    const idx = Number(k);
    if (!Number.isFinite(idx)) continue;
    next[String(idx >= at ? idx + 1 : idx)] = v;
  }
  return Object.keys(next).length ? next : undefined;
}

function remapRecordKeysOnColDelete<T>(
  record: Record<string, T> | undefined,
  at: number,
): Record<string, T> | undefined {
  if (!record) return record;
  const next: Record<string, T> = {};
  for (const [k, v] of Object.entries(record)) {
    const idx = Number(k);
    if (!Number.isFinite(idx)) continue;
    if (idx === at) continue;
    next[String(idx > at ? idx - 1 : idx)] = v;
  }
  return Object.keys(next).length ? next : undefined;
}

function remapRecordKeysOnRowInsert<T>(
  record: Record<string, T> | undefined,
  at: number,
): Record<string, T> | undefined {
  if (!record) return record;
  const next: Record<string, T> = {};
  for (const [k, v] of Object.entries(record)) {
    const idx = Number(k);
    if (!Number.isFinite(idx)) continue;
    next[String(idx >= at ? idx + 1 : idx)] = v;
  }
  return Object.keys(next).length ? next : undefined;
}

function remapRecordKeysOnRowDelete<T>(
  record: Record<string, T> | undefined,
  at: number,
): Record<string, T> | undefined {
  if (!record) return record;
  const next: Record<string, T> = {};
  for (const [k, v] of Object.entries(record)) {
    const idx = Number(k);
    if (!Number.isFinite(idx)) continue;
    if (idx === at) continue;
    next[String(idx > at ? idx - 1 : idx)] = v;
  }
  return Object.keys(next).length ? next : undefined;
}

function defaultColWidth(element: TableElement, colWidths: number[], col: number): number {
  return colWidths[col] ?? Math.max(TABLE_MIN_COL_WIDTH, Math.floor(element.size.width / element.data.cols));
}

function defaultRowHeight(element: TableElement, rowHeights: number[], row: number): number {
  return rowHeights[row] ?? Math.max(TABLE_MIN_ROW_HEIGHT, Math.floor(element.size.height / element.data.rows));
}

export function patchInsertColumn(
  element: TableElement,
  col: number,
  side: 'left' | 'right',
): TableStructurePatch {
  const at = side === 'left' ? col : col + 1;
  const colWidths = [...getColWidths(element)];
  const w = defaultColWidth(element, colWidths, Math.min(col, colWidths.length - 1));
  colWidths.splice(at, 0, w);
  const cells = remapCellsOnColInsert(element.data.cells, at);
  return {
    data: {
      cols: element.data.cols + 1,
      colWidths,
      cells,
      colBackgrounds: remapRecordKeysOnColInsert(element.data.colBackgrounds, at),
      mergedRanges: shiftMergedOnColInsert(element.data.mergedRanges, at),
    },
    size: { width: colWidths.reduce((a, b) => a + b, 0) },
  };
}

export function patchInsertColumnAtEnd(element: TableElement): TableStructurePatch {
  const colWidths = [...getColWidths(element)];
  const lastW = colWidths[colWidths.length - 1] ?? TABLE_MIN_COL_WIDTH;
  colWidths.push(lastW);
  const at = colWidths.length - 1;
  return {
    data: {
      cols: element.data.cols + 1,
      colWidths,
      cells: remapCellsOnColInsert(element.data.cells, at),
      colBackgrounds: remapRecordKeysOnColInsert(element.data.colBackgrounds, at),
      mergedRanges: shiftMergedOnColInsert(element.data.mergedRanges, at),
    },
    size: { width: colWidths.reduce((a, b) => a + b, 0) },
  };
}

export function patchDeleteColumn(element: TableElement, col: number): TableStructurePatch | null {
  if (element.data.cols <= 1) return null;
  const colWidths = [...getColWidths(element)];
  colWidths.splice(col, 1);
  return {
    data: {
      cols: element.data.cols - 1,
      colWidths,
      cells: remapCellsOnColDelete(element.data.cells, col),
      colBackgrounds: remapRecordKeysOnColDelete(element.data.colBackgrounds, col),
      mergedRanges: shiftMergedOnColDelete(element.data.mergedRanges, col),
    },
    size: { width: colWidths.reduce((a, b) => a + b, 0) },
  };
}

export function patchDuplicateColumn(element: TableElement, col: number): TableStructurePatch {
  const colWidths = [...getColWidths(element)];
  const w = colWidths[col] ?? TABLE_MIN_COL_WIDTH;
  const at = col + 1;
  colWidths.splice(at, 0, w);
  const cells = { ...(remapCellsOnColInsert(element.data.cells, at) ?? {}) };
  const rows = element.data.rows;
  for (let r = 0; r < rows; r++) {
    const src = getCell(element, r, col);
    if (src) cells[`${r},${at}`] = { ...src };
  }
  const colBg = element.data.colBackgrounds?.[String(col)];
  const colBackgrounds = remapRecordKeysOnColInsert(element.data.colBackgrounds, at);
  if (colBg && colBackgrounds) {
    colBackgrounds[String(at)] = colBg;
  }
  return {
    data: {
      cols: element.data.cols + 1,
      colWidths,
      cells: Object.keys(cells).length ? cells : undefined,
      colBackgrounds,
      mergedRanges: shiftMergedOnColInsert(element.data.mergedRanges, at),
    },
    size: { width: colWidths.reduce((a, b) => a + b, 0) },
  };
}

export function patchMoveColumn(
  element: TableElement,
  col: number,
  direction: 'left' | 'right',
): TableStructurePatch | null {
  const to = direction === 'left' ? col - 1 : col + 1;
  return patchMoveColumnTo(element, col, to);
}

export function patchMoveColumnTo(
  element: TableElement,
  from: number,
  to: number,
): TableStructurePatch | null {
  if (from === to || from < 0 || from >= element.data.cols || to < 0 || to >= element.data.cols) {
    return null;
  }
  const colWidths = getColWidths(element);
  const { colWidths: newW, cells } = reorderColumns(element, colWidths, from, to);
  return { data: { colWidths: newW, cells } };
}

export function patchInsertRow(
  element: TableElement,
  row: number,
  side: 'above' | 'below',
): TableStructurePatch {
  const at = side === 'above' ? row : row + 1;
  const rowHeights = [...getRowHeights(element)];
  const h = defaultRowHeight(element, rowHeights, Math.min(row, rowHeights.length - 1));
  rowHeights.splice(at, 0, h);
  return {
    data: {
      rows: element.data.rows + 1,
      rowHeights,
      cells: remapCellsOnRowInsert(element.data.cells, at),
      rowBackgrounds: remapRecordKeysOnRowInsert(element.data.rowBackgrounds, at),
      mergedRanges: shiftMergedOnRowInsert(element.data.mergedRanges, at),
    },
    size: { height: rowHeights.reduce((a, b) => a + b, 0) },
  };
}

export function patchInsertRowAtEnd(element: TableElement): TableStructurePatch {
  const rowHeights = [...getRowHeights(element)];
  const lastH = rowHeights[rowHeights.length - 1] ?? TABLE_MIN_ROW_HEIGHT;
  rowHeights.push(lastH);
  const at = rowHeights.length - 1;
  return {
    data: {
      rows: element.data.rows + 1,
      rowHeights,
      cells: remapCellsOnRowInsert(element.data.cells, at),
      rowBackgrounds: remapRecordKeysOnRowInsert(element.data.rowBackgrounds, at),
      mergedRanges: shiftMergedOnRowInsert(element.data.mergedRanges, at),
    },
    size: { height: rowHeights.reduce((a, b) => a + b, 0) },
  };
}

export function patchDeleteRow(element: TableElement, row: number): TableStructurePatch | null {
  if (element.data.rows <= 1) return null;
  const rowHeights = [...getRowHeights(element)];
  rowHeights.splice(row, 1);
  return {
    data: {
      rows: element.data.rows - 1,
      rowHeights,
      cells: remapCellsOnRowDelete(element.data.cells, row),
      rowBackgrounds: remapRecordKeysOnRowDelete(element.data.rowBackgrounds, row),
      mergedRanges: shiftMergedOnRowDelete(element.data.mergedRanges, row),
    },
    size: { height: rowHeights.reduce((a, b) => a + b, 0) },
  };
}

export function patchDuplicateRow(element: TableElement, row: number): TableStructurePatch {
  const rowHeights = [...getRowHeights(element)];
  const h = rowHeights[row] ?? TABLE_MIN_ROW_HEIGHT;
  const at = row + 1;
  rowHeights.splice(at, 0, h);
  const cells = { ...(remapCellsOnRowInsert(element.data.cells, at) ?? {}) };
  const cols = element.data.cols;
  for (let c = 0; c < cols; c++) {
    const src = getCell(element, row, c);
    if (src) cells[`${at},${c}`] = { ...src };
  }
  const rowBg = element.data.rowBackgrounds?.[String(row)];
  const rowBackgrounds = remapRecordKeysOnRowInsert(element.data.rowBackgrounds, at);
  if (rowBg && rowBackgrounds) {
    rowBackgrounds[String(at)] = rowBg;
  }
  return {
    data: {
      rows: element.data.rows + 1,
      rowHeights,
      cells: Object.keys(cells).length ? cells : undefined,
      rowBackgrounds,
      mergedRanges: shiftMergedOnRowInsert(element.data.mergedRanges, at),
    },
    size: { height: rowHeights.reduce((a, b) => a + b, 0) },
  };
}

export function patchMoveRow(
  element: TableElement,
  row: number,
  direction: 'up' | 'down',
): TableStructurePatch | null {
  const to = direction === 'up' ? row - 1 : row + 1;
  return patchMoveRowTo(element, row, to);
}

export function patchMoveRowTo(
  element: TableElement,
  from: number,
  to: number,
): TableStructurePatch | null {
  if (from === to || from < 0 || from >= element.data.rows || to < 0 || to >= element.data.rows) {
    return null;
  }
  const rowHeights = getRowHeights(element);
  const { rowHeights: newH, cells } = reorderRows(element, rowHeights, from, to);
  return { data: { rowHeights: newH, cells } };
}

/** 合并矩形选区：写入 mergedRanges，并按行优先拼接各格文本到左上角单元格 */
export function patchMergeCellRange(
  element: TableElement,
  r0: number,
  c0: number,
  r1: number,
  c1: number,
): TableStructurePatch | null {
  if (r0 === r1 && c0 === c1) return null;
  const mergedRanges = element.data.mergedRanges ?? [];
  const hasOverlap = mergedRanges.some(
    ([a0, a1, a2, a3]) => !(a2 < r0 || a0 > r1 || a3 < c0 || a1 > c1),
  );
  if (hasOverlap) return null;

  const nextRanges = [...mergedRanges, [r0, c0, r1, c1] as [number, number, number, number]];
  const cells = { ...(element.data.cells ?? {}) };
  const parts: string[] = [];
  for (let ri = r0; ri <= r1; ri++) {
    for (let ci = c0; ci <= c1; ci++) {
      const t = cells[`${ri},${ci}`]?.text?.content.trim();
      if (t) parts.push(t);
    }
  }
  const mergedText = parts.join('');
  const key = `${r0},${c0}`;
  const existing = cells[key];
  cells[key] = {
    ...existing,
    text: mergedText ? createTextContentData({ content: mergedText }) : undefined,
  };
  return { data: { mergedRanges: nextRanges, cells } };
}
